import { Helmet } from "@/pages/home/components/Helmet";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { Link, useParams } from "react-router-dom";
import { categories, comics } from "@/mocks/comics";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-950 mb-2">分类未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该分类不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-medium px-6 py-2.5 rounded-full whitespace-nowrap cursor-pointer transition-all hover:bg-primary-600">
              <i className="ri-home-4-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const featuredComics = categoryComics.filter((c) => c.featured);
  const otherComics = categoryComics.filter((c) => !c.featured);
  const allCategoryComics = [...featuredComics, ...otherComics];

  return (
    <>
      <Helmet
        title={`${category.name}韩国漫画-免费${category.name}韩漫下拉式阅读-韩漫头条`}
        description={`韩漫头条${category.name}分类提供${category.count}部免费${category.name}韩国漫画，无删减下拉式阅读，高清画质，极速更新。${category.name}韩漫爱好者首选平台。`}
        keywords={`${category.name}韩国漫画,${category.name}韩漫,免费${category.name}漫画,下拉式${category.name}漫画`}
        canonicalUrl={`http://uqdww.cn/category/${slug}`}
        ogTitle={`${category.name}韩国漫画-免费韩漫下拉式阅读`}
        ogDescription={`韩漫头条${category.name}分类提供${category.count}部免费${category.name}韩国漫画，无删减下拉式阅读。`}
        schemaJsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${category.name}韩国漫画`,
          "description": `韩漫头条${category.name}分类 - ${category.count}部${category.name}韩国漫画免费阅读`,
          "url": `http://uqdww.cn/category/${slug}`,
          "isPartOf": {
            "@type": "WebSite",
            "name": "韩漫头条",
            "url": "http://uqdww.cn/"
          }
        }}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[350px] md:min-h-[420px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20style%20abstract%20background%20with%20warm%20coral%20gradient%20and%20soft%20cream%20tones%2C%20subtle%20comic%20panel%20lines%2C%20artistic%20atmospheric%20mood%2C%20elegant%20minimal%20composition%2C%20soft%20diffused%20lighting&width=1920&height=500&seq=cat-hero-generic&orientation=landscape"
              alt={`${category.name}漫画`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/65"></div>
          </div>

          <div className="relative z-10 w-full pt-20">
            <div className="container-page">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-background-50/80 bg-background-50/10 backdrop-blur-sm px-3 py-1 rounded-full mb-4">
                  <i className={category.icon}></i> {category.name}分类
                </span>
                <h1 className="font-heading text-3xl md:text-5xl text-background-50 leading-tight">
                  免费{category.name}韩漫
                  <span className="block text-lg md:text-xl text-background-300/80 font-sans mt-2 font-normal">
                    共 {category.count} 部作品 · 无删减下拉式阅读
                  </span>
                </h1>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="container-page">
            <Breadcrumb items={[{ label: category.name, href: `/category/${slug}` }]} />
          </div>
        </section>

        {/* Comic Grid */}
        <section className="py-12 md:py-20 bg-background-50">
          <div className="container-page">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
                全部{category.name}漫画
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序：</span>
                <select className="text-xs text-foreground-700 bg-background-100 border border-background-200/70 rounded-lg px-2 py-1.5 cursor-pointer outline-none">
                  <option>最近更新</option>
                  <option>评分最高</option>
                  <option>人气最热</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {allCategoryComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.slug}`}
                  className="group block bg-background-100 rounded-xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                      comic.status === "连载中"
                        ? "bg-accent-500/90 text-background-50"
                        : "bg-foreground-700/80 text-background-50"
                    }`}>
                      {comic.status}
                    </span>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent h-16"></div>
                    <span className="absolute bottom-2 left-2 text-background-50/80 text-[10px] font-medium">
                      ★ {comic.rating}
                    </span>
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-950 truncate">{comic.title}</h3>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-foreground-600">{comic.author}</span>
                      <span className="text-xs text-primary-500 font-medium whitespace-nowrap">{comic.latestChapter}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-primary-50/50">
          <div className="container-page text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              还没找到喜欢的？
            </h2>
            <p className="text-foreground-500 text-sm max-w-md mx-auto mb-6">
              我们还有更多分类等你探索，总有一部韩漫适合你
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {categories.filter((c) => c.slug !== slug).slice(0, 6).map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-50 text-foreground-700 text-sm cursor-pointer hover:bg-primary-500 hover:text-background-50 transition-all whitespace-nowrap"
                >
                  <i className={`${c.icon} text-xs`}></i> {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}